import { apiSlice } from "../../app/apiSlice";

export const bundleSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllBundles: builder.query({
      query: ({ limit = 9, offset = 0, searchText = "" } = {}) => ({
        url: "/api/v1/en/student/bundle/get-all",
        method: "GET",
        params: {
          limit,
          offset,
          ...(searchText && { searchText }),
        },
      }),
      providesTags: ["Bundles"],
    }),

    // View single bundle by ID
    getBundleById: builder.query({
      query: (bundleId) => ({
        url: `/api/v1/en/student/bundle/view/${bundleId}`,
        method: "GET",
      }),
      providesTags: (result, error, bundleId) => [
        { type: "Bundles", id: bundleId },
      ],
    }),

    // Get bundle test by ID (similar to getMockTestById)
    getBundleTestById: builder.query({
      query: (testId) => ({
        url: `/api/v1/en/student/bundle/test/${testId}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return response?.data?.data?.mockTests ?? response?.data?.data ?? null;
      },
      providesTags: (result, error, testId) => [
        { type: "BundleTests", id: testId },
      ],
    }),

    getTeacherTimeSlots: builder.query({
      query: ({ bundleId, studentBundleId, module }) => ({
        url: `/api/v1/en/student/one-to-one/available-slots`,
        method: "GET",
        params: {
          bundleId,
          studentBundleId,
          module,
        },
      }),
      providesTags: ["TeacherTimeSlots"],
    }),

    bookSession: builder.mutation({
      query: (sessionData) => ({
        url: "/api/v1/en/student/one-to-one/book-slot",
        method: "PATCH",
        body: sessionData,
      }),
      invalidatesTags: ["Bundles", "TeacherTimeSlots", "BookedSlots"],
    }),

    getBookedSlots: builder.query({
      query: () => ({
        url: "/api/v1/en/student/one-to-one/booked-slots",
        method: "GET",
      }),
      providesTags: ["BookedSlots"],
    }),

    // Get bundle test IDs for a specific test type and part
    getBundleTestIds: builder.query({
      query: ({
        testType,
        listeningTestId,
        readingTestId,
        writingTestId,
        speakingTestId,
        readingPartId,
        readingSectionId,
        listeningPartId,
        listeningSectionId,
        writingPartId,
        speakingPartId,
      }) => {
        const params = new URLSearchParams();
        if (testType) params.append("testType", testType);
        if (listeningTestId) params.append("listeningTestId", listeningTestId);
        if (readingTestId) params.append("readingTestId", readingTestId);
        if (writingTestId) params.append("writingTestId", writingTestId);
        if (speakingTestId) params.append("speakingTestId", speakingTestId);

        // Handle arrays for part IDs
        if (readingPartId) {
          if (Array.isArray(readingPartId)) {
            readingPartId.forEach((id) => params.append("readingPartId", id));
          } else {
            params.append("readingPartId", readingPartId);
          }
        }
        // Handle IELTS reading section ID
        if (readingSectionId)
          params.append("readingSectionId", readingSectionId);

        if (listeningPartId) {
          if (Array.isArray(listeningPartId)) {
            listeningPartId.forEach((id) =>
              params.append("listeningPartId", id),
            );
          } else {
            params.append("listeningPartId", listeningPartId);
          }
        }
        // Handle IELTS listening section ID
        if (listeningSectionId)
          params.append("listeningSectionId", listeningSectionId);

        if (writingPartId) params.append("writingPartId", writingPartId);
        if (speakingPartId) params.append("speakingPartId", speakingPartId);

        return {
          url: `/api/v1/en/student/bundle/get-bundle-test-ids?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["BundleTestIds"],
    }),

    // Get bundle test data (matches getPracticeTestData signature for drop-in replacement)
    getBundleTestData: builder.query({
      query: ({
        studentAttemptId,
        listeningId,
        readingId,
        speakingId,
        speakingCardId,
        writingId,
        listeningPartIds,
        readingPartIds,
        readingQuestionIds,
        currentSection, // 'listening', 'reading', 'writing', or 'speaking'
      }) => {
        const params = new URLSearchParams();

        // Student attempt ID is required
        if (!studentAttemptId) {
          throw new Error("studentAttemptId is required for bundle test data");
        }
        params.append("studentAttemptId", studentAttemptId);

        if (currentSection === "speaking") {
          if (speakingId) params.append("speakingId", speakingId);
          if (speakingCardId) params.append("speakingCardId", speakingCardId);
        } else if (currentSection === "listening") {
          // Only include listening parameters
          if (listeningId) params.append("listeningId", listeningId);
          // Add listening part IDs as arrays
          if (listeningPartIds && listeningPartIds.length > 0) {
            listeningPartIds.forEach((id) =>
              params.append("listeningPartId", id),
            );
          }
        } else if (currentSection === "reading") {
          // Only include reading parameters
          if (readingId) params.append("readingId", readingId);
          // Add reading part IDs and question IDs as arrays
          if (readingPartIds && readingPartIds.length > 0) {
            readingPartIds.forEach((id) => params.append("readingPartId", id));
          }
          if (readingQuestionIds && readingQuestionIds.length > 0) {
            readingQuestionIds.forEach((id) =>
              params.append("readingQuestionId", id),
            );
          }
        } else if (currentSection === "writing") {
          // Only include writing parameters
          if (writingId) params.append("writingId", writingId);
        } else {
          // If no current section specified, include all parameters
          if (listeningId) params.append("listeningId", listeningId);
          if (readingId) params.append("readingId", readingId);
          if (writingId) params.append("writingId", writingId);
          if (speakingId) params.append("speakingId", speakingId);

          // Add part IDs and question IDs as arrays
          if (listeningPartIds && listeningPartIds.length > 0) {
            listeningPartIds.forEach((id) =>
              params.append("listeningPartId", id),
            );
          }
          if (readingPartIds && readingPartIds.length > 0) {
            readingPartIds.forEach((id) => params.append("readingPartId", id));
          }
          if (readingQuestionIds && readingQuestionIds.length > 0) {
            readingQuestionIds.forEach((id) =>
              params.append("readingQuestionId", id),
            );
          }
        }

        console.log(
          `Fetching bundle test data for section '${currentSection || "all"}':`,
          params.toString(),
        );

        return {
          url: `/api/v1/en/student/bundle/get-bundle-test-data?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => {
        console.log("Bundle test data response:", response);
        return response.data || {};
      },
      providesTags: ["BundleTestData"],
      keepUnusedDataFor: 0, // Don't cache data
    }),

    // Get bundle practice test data
    getBundlePracticeTestData: builder.query({
      query: ({
        studentAttemptId,
        currentSection,
        listeningId,
        readingId,
        writingId,
        listeningPartIds,
        readingPartIds,
        readingQuestionIds,
      }) => {
        const params = new URLSearchParams();
        params.append("testType", "oet");

        if (studentAttemptId)
          params.append("studentAttemptId", studentAttemptId);
        if (currentSection) params.append("currentSection", currentSection);
        if (listeningId) params.append("listeningId", listeningId);
        if (readingId) params.append("readingId", readingId);
        if (writingId) params.append("writingId", writingId);

        if (listeningPartIds && listeningPartIds.length > 0) {
          listeningPartIds.forEach((id) =>
            params.append("listeningPartIds", id),
          );
        }
        if (readingPartIds && readingPartIds.length > 0) {
          readingPartIds.forEach((id) => params.append("readingPartIds", id));
        }
        if (readingQuestionIds && readingQuestionIds.length > 0) {
          readingQuestionIds.forEach((id) =>
            params.append("readingQuestionIds", id),
          );
        }

        return {
          url: `/api/v1/en/student/bundle/get-practice-test-data?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["BundlePracticeTestData"],
    }),

    // Create bundle test attempt (same as mock test pattern)
    createBundleTestAttempt: builder.mutation({
      query: ({
        bundleId,
        testIds,
        mockTestId,
        isPracticeMode,
        ieltsTest,
        oetTest,
        readingMinutes,
        oetReadingPartAMinutes,
        oetReadingPartBCMinutes,
        writingMinutes,
        speakingMinutes,
      }) => {
        const body = {
          bundleId,
          isPracticeMode,
          ...(testIds && { testIds }),
          ...(mockTestId && { mockTestId }),
          ...(ieltsTest && { ieltsTest }),
          ...(oetTest && { oetTest }),
        };

        if (isPracticeMode) {
          if (readingMinutes) body.readingMinutes = readingMinutes;
          if (oetReadingPartAMinutes)
            body.oetReadingPartAMinutes = oetReadingPartAMinutes;
          if (oetReadingPartBCMinutes)
            body.oetReadingPartBCMinutes = oetReadingPartBCMinutes;
          if (writingMinutes) body.writingMinutes = writingMinutes;
          if (speakingMinutes) body.speakingMinutes = speakingMinutes;
        }

        return {
          url: "/api/v1/en/student/test-attempt/create-bundle-attempt",
          method: "POST",
          body,
        };
      },
      transformResponse: (response) => {
        const studentAttemptId =
          response?.data?.data?.createStudentAttempt?._id;
        const ieltsTest = response?.data?.data?.createStudentAttempt?.ieltsTest;
        const oetTest = response?.data?.data?.createStudentAttempt?.oetTest;
        const writingId = ieltsTest?.writingId || oetTest?.writingId;

        return {
          studentAttemptId,
          writingId,
          ieltsTest,
          oetTest,
          fullResponse: response.data,
        };
      },
      invalidatesTags: ["Bundles", "BundleTestIds"],
    }),

    // Get bundle test PDF
    getBundleTestPdf: builder.query({
      query: ({ testId, module }) => {
        const params = new URLSearchParams();
        if (module) params.append("module", module);
        return {
          url: `/api/v1/en/student/mock-test/get-test-pdf/${testId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => {
        // Extract the PDF data from nested structure
        const pdfData = response?.data?.data;
        return {
          pdfUrl: pdfData?.pdfUrl,
          answersKeysPdfUrl: pdfData?.answersKeysPdfUrl,
          testId: pdfData?.testId,
          module: pdfData?.module,
        };
      },
      providesTags: ["BundleTestPdf"],
    }),

    // Get all test attempts for a specific test in a bundle
    getAllBundleTestAttemptsByTestId: builder.query({
      query: ({ testId, module, limit = 10, offset = 0 }) => {
        const params = new URLSearchParams();
        if (module) params.append("module", module);
        params.append("limit", limit.toString());
        params.append("offset", offset.toString());
        return `/api/v1/en/student/test-attempt/list-by-test/${testId}?${params.toString()}`;
      },
      transformResponse: (response) => ({
        list: response?.data?.data?.testAttempt ?? [],
        total: response?.data?.data?.dataCount ?? 0, // dataCount is a direct number, not nested
      }),
      providesTags: (result, error, params) => [
        { type: "BundleTestAttempts", id: `${params.testId}-${params.module}` },
      ],
    }),
  }),
});

export const {
  useGetAllBundlesQuery,
  useGetBundleByIdQuery,
  useLazyGetBundleByIdQuery,
  useGetBundleTestByIdQuery,
  useLazyGetBundleTestByIdQuery,
  useGetTeacherTimeSlotsQuery,
  useLazyGetTeacherTimeSlotsQuery,
  useBookSessionMutation,
  useGetBookedSlotsQuery,
  useLazyGetBookedSlotsQuery,
  useGetBundleTestIdsQuery,
  useLazyGetBundleTestIdsQuery,
  useGetBundleTestDataQuery,
  useLazyGetBundleTestDataQuery,
  useGetBundlePracticeTestDataQuery,
  useLazyGetBundlePracticeTestDataQuery,
  useCreateBundleTestAttemptMutation,
  useGetBundleTestPdfQuery,
  useGetAllBundleTestAttemptsByTestIdQuery,
  useLazyGetAllBundleTestAttemptsByTestIdQuery,
} = bundleSlice;
